import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuthStore } from '@/stores/authStore';
import { useCanvasStore } from '@/stores/canvasStore';
import {
  connectSocket,
  disconnectSocket,
  triggerAdminArchive,
  triggerAdminCanvasExpansion,
} from '@/services/socket';
import { showSnackbar } from '@/stores/snackbarStore';
import { ConfirmModal } from '@/components/ConfirmModal';

export function AdminPanel() {
  const user = useAuthStore((state) => state.user);
  const isConnected = useCanvasStore((state) => state.isConnected);
  const yoloMode = useCanvasStore((state) => state.yoloMode);
  const setYoloMode = useCanvasStore((state) => state.setYoloMode);
  const [modalOpen, setModalOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showExpandConfirm, setShowExpandConfirm] = useState(false);

  const handleClose = useCallback(() => setModalOpen(false), []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    },
    [handleClose]
  );

  useEffect(() => {
    if (modalOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [modalOpen, handleKeyDown]);

  // Only show for admins
  if (!user?.adminRole || !['SUPER_ADMIN', 'ADMIN'].includes(user.adminRole)) {
    return null;
  }

  const handleToggleConnection = () => {
    if (isConnected) {
      disconnectSocket();
      showSnackbar('Manually disconnected from server', 'info', 3000);
    } else {
      connectSocket();
    }
  };

  const handleArchiveClick = () => {
    setShowArchiveConfirm(true);
  };

  const handleExpandClick = () => {
    setShowExpandConfirm(true);
  };

  const handleConfirmArchive = () => {
    setShowArchiveConfirm(false);
    setIsArchiving(true);
    triggerAdminArchive();
    setTimeout(() => setIsArchiving(false), 2000);
  };

  const handleCancelArchive = () => {
    setShowArchiveConfirm(false);
  };

  const handleConfirmExpand = () => {
    setShowExpandConfirm(false);
    setIsExpanding(true);
    triggerAdminCanvasExpansion();
    setTimeout(() => setIsExpanding(false), 2000);
  };

  const handleCancelExpand = () => {
    setShowExpandConfirm(false);
  };

  const handleToggleYolo = () => {
    const newValue = !yoloMode;
    setYoloMode(newValue);
    showSnackbar(
      newValue ? 'YOLO Mode ON - admin painting' : 'YOLO Mode OFF',
      newValue ? 'warning' : 'info',
      3000
    );
  };

  return (
    <>
      {/* Compact trigger */}
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className="flex items-center justify-between gap-2 min-w-26 px-2.5 py-1.5 rounded-lg bg-wall-surface/80 backdrop-blur-xs border border-wall-accent/30 text-wall-accent hover:border-wall-accent/50 hover:bg-wall-surface transition-colors shadow-lg"
        title={yoloMode ? 'Admin (YOLO mode on)' : 'Admin actions'}
        aria-label="Open admin panel"
      >
        <span className="flex items-center gap-1.5">
          <span className="text-sm">👑</span>
          <span className="text-xs font-medium inline">Admin</span>
        </span>
        {yoloMode && (
          <span className="text-[10px] leading-none shrink-0" title="YOLO mode on">
            🚀
          </span>
        )}
      </button>

      {/* Admin actions modal */}
      {modalOpen &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-xs"
              onClick={handleClose}
              aria-hidden
            />
            <div
              className="relative bg-wall-surface border border-wall-border rounded-xl shadow-2xl w-full max-w-sm animate-scale-in"
              role="dialog"
              aria-modal="true"
              aria-labelledby="admin-modal-title"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-wall-border/50">
                <div className="flex items-center gap-2">
                  <span className="text-wall-accent text-lg">👑</span>
                  <h2 id="admin-modal-title" className="text-lg font-semibold text-wall-text">
                    Admin
                  </h2>
                  <span className="text-[10px] uppercase tracking-wide text-wall-muted font-medium px-1.5 py-0.5 rounded bg-wall-border/50">
                    {user.adminRole}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="p-1.5 rounded-lg text-wall-muted hover:text-wall-text hover:bg-wall-border/30 transition-colors"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Actions */}
              <div className="p-4 space-y-3">
                {/* YOLO Mode */}
                <button
                  type="button"
                  onClick={handleToggleYolo}
                  className={`w-full px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-between gap-2 ${
                    yoloMode
                      ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                      : 'bg-wall-bg/80 text-wall-muted border border-wall-border hover:border-wall-accent/30'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span>{yoloMode ? '🚀' : '⏱️'}</span>
                    YOLO Mode
                  </span>
                  <span className="text-xs font-semibold">{yoloMode ? 'ON' : 'OFF'}</span>
                </button>

                {/* Connection */}
                <button
                  type="button"
                  onClick={handleToggleConnection}
                  className={`w-full px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-between gap-2 ${
                    isConnected
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span
                      className={`w-2.5 h-2.5 rounded-full ${isConnected ? 'bg-emerald-400' : 'bg-red-400'} animate-pulse`}
                    />
                    Connection
                  </span>
                  <span className="text-xs font-semibold">
                    {isConnected ? 'Connected' : 'Disconnected'}
                  </span>
                </button>

                {/* Manual expansion */}
                <button
                  type="button"
                  onClick={handleExpandClick}
                  disabled={isExpanding || !isConnected}
                  className={`w-full px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                    isExpanding || !isConnected
                      ? 'bg-wall-border/30 text-wall-muted cursor-not-allowed'
                      : 'bg-sky-500/20 text-sky-300 border border-sky-500/40 hover:bg-sky-500/30'
                  }`}
                >
                  {isExpanding ? (
                    <span className="animate-pulse">Expanding...</span>
                  ) : (
                    <>
                      <span>💥</span>
                      Expand canvas now
                    </>
                  )}
                </button>

                {/* Archive */}
                <button
                  type="button"
                  onClick={handleArchiveClick}
                  disabled={isArchiving || !isConnected}
                  className={`w-full px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                    isArchiving || !isConnected
                      ? 'bg-wall-border/30 text-wall-muted cursor-not-allowed'
                      : 'bg-linear-to-r from-wall-accent to-wall-accent-secondary hover:opacity-90 text-white'
                  }`}
                >
                  {isArchiving ? (
                    <span className="animate-pulse">Archiving...</span>
                  ) : (
                    <>
                      <span>📸</span>
                      Archive & reset canvas
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <ConfirmModal
        isOpen={showArchiveConfirm}
        title="Archive & Reset Canvas"
        message="This will save the current canvas to the gallery and clear it for a fresh start. The current canvas size will be preserved. Are you sure?"
        confirmText="Archive Now"
        cancelText="Cancel"
        confirmVariant="primary"
        onConfirm={handleConfirmArchive}
        onCancel={handleCancelArchive}
      />
      <ConfirmModal
        isOpen={showExpandConfirm}
        title="Expand Canvas Now"
        message="This will permanently expand the current canvas by the configured amount on every side and show the expansion animation to connected users. Expansion achievements will not be awarded."
        confirmText="Expand Now"
        cancelText="Cancel"
        confirmVariant="primary"
        isLoading={isExpanding}
        onConfirm={handleConfirmExpand}
        onCancel={handleCancelExpand}
      />
    </>
  );
}
